// vaelHands.js — V'AEL'S FINGERS  (caP'n, 2026-09-24: the acting pass)
// The sculpt's fingers ride 15 bones per hand (vael_fingers.py). A finger
// curls about its own axis; the table is printed by the builder's
// curl_table() — the axis in glTF space and the sculpted rest curl of each
// segment, so a shape can ask for ABSOLUTE bends (degrees from straight).
import * as THREE from "three";

export const CURL = {"indexL": {"axis": [0.0, -0.0599, -0.9982], "rest": [10, 32, 52]}, "middleL": {"axis": [0.0, -0.02, -0.9998], "rest": [12, 36, 58]}, "ringL": {"axis": [0.0, 0.04, -0.9992], "rest": [14, 40, 62]}, "pinkyL": {"axis": [0.0, 0.0896, -0.996], "rest": [18, 46, 70]}, "thumbL": {"axis": [0.0, -0.3194, -0.9476], "rest": [0, 0, 0]}, "indexR": {"axis": [0.0, 0.0599, 0.9982], "rest": [10, 32, 52]}, "middleR": {"axis": [0.0, 0.02, 0.9998], "rest": [12, 36, 58]}, "ringR": {"axis": [0.0, -0.04, 0.9992], "rest": [14, 40, 62]}, "pinkyR": {"axis": [0.0, -0.0896, 0.996], "rest": [18, 46, 70]}, "thumbR": {"axis": [0.0, 0.3194, 0.9476], "rest": [0, 0, 0]}};
const FINGERS = ["index", "middle", "ring", "pinky"];

// HAND SHAPES — each finger's three segments as ABSOLUTE bends from straight
// (cumulative °), the thumb as its own three local bends + a swing across the palm.
// null = the sculpt's rest.
export const HANDS = {
  relaxed: null,
  // the fist's last joints stop short of the palm: pressed deeper, the fingertips
  // sink in and the ink hull shows through the gap as a black hole (2026-09-24 QA)
  fist:   { f: { index: [90, 160, 200], middle: [92, 162, 202], ring: [94, 164, 204], pinky: [96, 166, 206] }, thumb: [20, 30, 20, 45] },
  point:  { f: { index: [2, 4, 6], middle: [92, 162, 202], ring: [94, 164, 204], pinky: [96, 166, 206] }, thumb: [22, 30, 18, 45] },
  open:   { f: { index: [0, 6, 12], middle: [0, 5, 10], ring: [2, 8, 14], pinky: [4, 12, 18] }, thumb: [-8, 0, 0] },
  soft:   { f: { index: [12, 30, 44], middle: [14, 32, 46], ring: [16, 36, 50], pinky: [18, 40, 56] }, thumb: [0, 8, 6] },
  grip:   { f: { index: [62, 140, 200], middle: [66, 146, 206], ring: [70, 152, 212], pinky: [74, 158, 218] }, thumb: [18, 26, 14] },
  pinch:  { f: { index: [48, 110, 150], middle: [58, 140, 200], ring: [70, 160, 220], pinky: [78, 170, 230] }, thumb: [22, 30, 18] },
  beckon: { f: { index: [8, 30, 50], middle: [70, 150, 195], ring: [80, 160, 200], pinky: [86, 166, 206] }, thumb: [18, 26, 16, 35] },
  // cradling (a chin, an elbow, a cup): every finger softly round something (pose-refinement 2026-09-27)
  cup:    { f: { index: [26, 60, 88], middle: [28, 64, 92], ring: [30, 68, 96], pinky: [32, 72, 102] }, thumb: [8, 16, 10] },
  claw:   { f: { index: [30, 80, 115], middle: [32, 84, 120], ring: [34, 88, 124], pinky: [36, 92, 128] }, thumb: [6, 14, 10] },
};

const _q = new THREE.Quaternion(), _q2 = new THREE.Quaternion(), _a = new THREE.Vector3(), _l = new THREE.Vector3();
// the thumb also SWINGS about the hand's long axis (a 4th number, degrees): that is
// how a thumb folds across the front of a fist rather than just bending sideways
const LONG = { L: [0, 1, 0], R: [0, -1, 0] };
const D2R = Math.PI / 180;
function localBends(abs, rest) {
  const r1 = abs[0] - rest[0];
  const r2 = abs[1] - rest[1] - r1;
  const r3 = abs[2] - rest[2] - r1 - r2;
  return [r1, r2, r3];
}
// the local bends of one shape, cached: {name+side: [r1, r2, r3]}
const CACHE = new Map();
function bendsOf(shape, k) {
  const key = shape + k;
  let b = CACHE.get(key);
  if (b) return b;
  b = {};
  const S = HANDS[shape];
  for (const f of FINGERS) b[f] = S ? localBends(S.f[f], CURL[f + k].rest) : [0, 0, 0];
  b.thumb = S ? S.thumb : [0, 0, 0];
  CACHE.set(key, b);
  return b;
}
// pose one hand: a blend of two shapes (from → to by t), plus an optional extra
// curl on some fingers (a beckon's flick). Writes quaternions; never allocates.
export function poseHand(B, k, from, to, t = 1, extra = null) {
  const A = bendsOf(from || "relaxed", k), Z = bendsOf(to || "relaxed", k);
  for (const f of [...FINGERS, "thumb"]) {
    const ax = CURL[f + k].axis;
    _a.set(ax[0], ax[1], ax[2]);
    for (let i = 0; i < 3; i++) {
      const bone = B[f + k + (i + 1)];
      if (!bone) continue;
      let deg = A[f][i] + (Z[f][i] - A[f][i]) * t;
      if (extra && extra[f]) deg += extra[f][i] || 0;
      bone.quaternion.copy(_q.setFromAxisAngle(_a, deg * D2R));
      if (f === "thumb" && i === 0) {
        const sw = (A.thumb[3] || 0) + ((Z.thumb[3] || 0) - (A.thumb[3] || 0)) * t;
        if (sw) { _l.set(...LONG[k]); bone.quaternion.premultiply(_q2.setFromAxisAngle(_l, sw * D2R)); }
      }
    }
  }
}
